// /capture — a bare, chrome-free route that renders ONE chart so the
// report-service headless browser can screenshot it server-side.
//
// Contract (driven by report-service — see hnapi/docs/report-render-service-spec.md):
//   window.__renderChart(spec)  -> begins rendering one chart
//   window.__captureState       -> 'idle' | 'rendering' | 'ready' | 'error'
//   window.__captureError       -> message when state === 'error'
//   [data-capture-root]         -> element the service screenshots
//
// SCAFFOLD: the window contract, routing, layout and readiness signal are
// complete. Auth plumbing (how `apiClient` picks up spec.authToken) and the
// `nps`/`sentiment`/`competitive`/`pricing` kinds are marked TODO — finish per
// the spec. Reuse the chart selection already in
// components/ReportGenerator/ReportChartRenderer.js where helpful.

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { AuthContext } from '../components/AuthContext';
import PostAnalytics from '../components/PostAnalytics';
import PostLikesAnalytics from '../components/PostLikesAnalytics';
import DailyLeaverJoiner from '../components/DailyLeaverJoiner';
import SentimentAnalysisChart from '../components/SentimentAnalysisChart';

// Mark the page state so the headless driver can poll it.
function setState(state, error) {
    window.__captureState = state;
    window.__captureError = error || '';
}

// Resolve once a chart node has real layout — replaces the brittle fixed delay
// the in-browser capture used. `count` lets a grid wait for all of its charts.
function waitForCharts(root, count = 1, timeoutMs = 25000) {
    return new Promise((resolve, reject) => {
        const started = Date.now();
        const tick = () => {
            const nodes = root.querySelectorAll('.recharts-wrapper, canvas');
            let ready = 0;
            nodes.forEach(n => { if (n.offsetWidth > 0 && n.offsetHeight > 0) ready++; });
            if (ready >= count) { resolve(); return; }
            if (Date.now() - started > timeoutMs) { reject(new Error('charts did not render in time')); return; }
            setTimeout(tick, 200);
        };
        tick();
    });
}

export default function CaptureChart() {
    const [spec, setSpec] = useState(null);
    const rootRef = useRef(null);

    // Expose window.__renderChart for the headless driver.
    useEffect(() => {
        setState('idle');
        window.__renderChart = (incoming) => {
            setState('rendering');
            setSpec(incoming || null);
        };
        return () => { delete window.__renderChart; };
    }, []);

    // After the chart mounts, wait for it to actually render, then flip to ready.
    const onMounted = useCallback(async (chartCount) => {
        try {
            // Allow data fetches + a paint settle before measuring.
            await new Promise(r => setTimeout(r, 600));
            await waitForCharts(rootRef.current, chartCount);
            await new Promise(r => setTimeout(r, 400)); // let the final paint flush
            setState('ready');
        } catch (e) {
            setState('error', e?.message || String(e));
        }
    }, []);

    if (!spec) {
        return <div data-capture-root ref={rootRef} style={{ background: '#fff' }} />;
    }

    // AuthContext value the chart components need (they call hnapi via apiClient).
    // TODO(report-service): ensure `services/apiClient` actually uses
    // spec.authToken — apiClient may need an explicit init call here.
    const authValue = {
        userId: 'report-service',
        token: spec.authToken,
        serverAddress: spec.serverAddress,
        flaskServerAddress: spec.flaskServerAddress,
    };

    const topicMap = spec.topicId
        ? { [spec.topicId]: { id: spec.topicId, name: spec.topicName, priority: 0 } }
        : {};

    let content = null;
    let chartCount = 1;

    if (spec.kind === 'postVolume') {
        content = (
            <div style={{ width: 1180 }}>
                <PostAnalytics
                    topicMap={topicMap} companyName={spec.companyName} autoSearch
                    initialSource={spec.source} initialStartDate={spec.startDate}
                    initialEndDate={spec.endDate} initialBucket={spec.bucket || 'week'}
                    initialRollingDays={30}
                />
            </div>
        );
    } else if (spec.kind === 'postVolumeGrid') {
        const cells = spec.sources || [];
        chartCount = cells.length;
        const cols = Math.min(3, Math.max(1, Math.ceil(Math.sqrt(cells.length || 1))));
        content = (
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1180px)`, gap: 20, padding: 20 }}>
                {cells.map((c, i) => (
                    <div key={i} style={{ width: 1180 }}>
                        <PostAnalytics
                            topicMap={topicMap} companyName={spec.companyName} autoSearch
                            initialSource={c.source} initialStartDate={c.startDate}
                            initialEndDate={spec.endDate} initialBucket="week" initialRollingDays={30}
                        />
                    </div>
                ))}
            </div>
        );
    } else if (spec.kind === 'postLikes') {
        content = (
            <div style={{ width: 1180 }}>
                <PostLikesAnalytics
                    topicMap={topicMap} companyName={spec.companyName} autoSearch
                    initialStartDate={spec.startDate} initialEndDate={spec.endDate}
                    initialBucket={spec.bucket || 'week'}
                />
            </div>
        );
    } else if (spec.kind === 'leaverJoiner') {
        content = (
            <div style={{ width: 1180 }}>
                <DailyLeaverJoiner
                    topicMap={topicMap} companyName={spec.companyName} initialTimeRange="1Y"
                />
            </div>
        );
    } else if (spec.kind === 'nps' || spec.kind === 'sentiment') {
        // Area chart + metric boxes (NPS Score / Peak / Low / Posts Analyzed) —
        // exactly the Sentiment Breakdown view. `hideDetails` drops the
        // snapshot card + posts list so the capture is just chart + boxes.
        content = (
            <div style={{ width: 1180 }}>
                <SentimentAnalysisChart
                    topicId={spec.topicId}
                    type={spec.kind === 'nps' ? 'NPS' : spec.assessmentType}
                    productName={spec.topicName}
                    hideHeader
                    hideDetails
                    timeRange={spec.timeRange || 12}
                />
            </div>
        );
    } else {
        // TODO(report-service): competitive / pricing — need data passed via spec.data.
        setState('error', `kind "${spec.kind}" not implemented in the capture scaffold`);
        return <div data-capture-root ref={rootRef} style={{ background: '#fff' }} />;
    }

    return (
        <AuthContext.Provider value={authValue}>
            <div
                data-capture-root
                ref={rootRef}
                style={{ background: '#fff', display: 'inline-block' }}
            >
                <ChartMount onMounted={() => onMounted(chartCount)}>{content}</ChartMount>
            </div>
        </AuthContext.Provider>
    );
}

// Fires onMounted once after the chart subtree first commits.
function ChartMount({ children, onMounted }) {
    useEffect(() => { onMounted(); }, [onMounted]);
    return children;
}
